import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { GuidancePanel } from './GuidancePanel';
import { PageHeader } from './PageHeader';
import { HelpModal } from './HelpModal';
import { DesktopInstructions } from './DesktopInstructions';
import { MobileInstructions } from './MobileInstructions';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import type { Consultation, TranslationMap} from '../types/consultation';
import { trackWizardStarted, trackStepView, trackWizardCompleted, trackScreenView } from '../utils/analytics';
import { submitCompletionToEN } from '../services/enAjaxSubmission';
import { hasCompletionField } from '../config/enFormConfig';
import { appendLanguageToEuFormUrl } from '../utils/urlEncoding';

interface MainApplicationProps {
  config: Consultation;
  translations: TranslationMap;
  onComplete: () => void;
  userCountry?: string;
  onNavigateToHowToUse?: () => void;
}

export function MainApplication({ config, translations, onComplete, userCountry, onNavigateToHowToUse }: MainApplicationProps) {
  const { t, i18n } = useTranslation('application');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const hasTrackedScreenView = useRef(false);
  const consultationStartTime = useRef<number | null>(null);

  const currentStep = config.steps[currentQuestionIndex];
  const currentTranslation = translations[currentStep.questionId];
  const isLastQuestion = currentQuestionIndex === config.steps.length - 1;

  // Get consultation ID from root element or config
  const consultationId = config.consultationId || (() => {
    const rootElement = document.getElementById('consultation-wizard-root');
    return rootElement?.getAttribute('data-consultation-id') || 'unknown';
  })();

  // Track screen view and wizard started when component mounts (only once)
  useEffect(() => {
    if (config && !hasTrackedScreenView.current) {
      trackScreenView('application', consultationId, 3);
      trackWizardStarted(consultationId);
      hasTrackedScreenView.current = true;
    }
  }, [config, consultationId]);

  // Track step view when question index changes
  useEffect(() => {
    if (config && currentStep) {
      trackStepView(currentQuestionIndex + 1, currentStep.questionId);
    }
    // Only depend on currentQuestionIndex and config - currentStep is derived from these
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, config]);

  // Scroll to top when question changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentQuestionIndex]);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem('consultationProgress', currentQuestionIndex.toString());
  }, [currentQuestionIndex]);

  // Load saved progress on mount
  useEffect(() => {
    const saved = localStorage.getItem('consultationProgress');
    if (saved) {
      setCurrentQuestionIndex(parseInt(saved, 10));
    }
    // Restore start time from sessionStorage if it exists
    const savedStartTime = sessionStorage.getItem('consultationStartTime');
    if (savedStartTime) {
      consultationStartTime.current = parseInt(savedStartTime, 10);
    }
  }, []);

  // Initialize timer when first question is reached
  useEffect(() => {
    if (currentQuestionIndex === 0 && consultationStartTime.current === null) {
      consultationStartTime.current = Date.now();
      // Store in sessionStorage for persistence across navigation
      sessionStorage.setItem('consultationStartTime', consultationStartTime.current.toString());
    }
  }, [currentQuestionIndex]);

  const handleOpenConsultation = () => {
    const currentLanguage = i18n.language || 'en';
    const urlWithLanguage = appendLanguageToEuFormUrl(config.euFormUrl, currentLanguage);
    window.open(urlWithLanguage, '_blank', 'noopener,noreferrer');
  };

  const handleNext = () => {
    if (isLastQuestion) {
      // Show confirmation modal before completing
      setShowConfirmationModal(true);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleConfirmCompletion = () => {
    // Track wizard completion
    if (config) {
      trackWizardCompleted(consultationId, config.steps.length);
    }
    localStorage.removeItem('consultationProgress');
    setShowConfirmationModal(false);
    
    // Submit completion data to EN (async, non-blocking)
    try {
      // Get start time (from ref or sessionStorage)
      const startTime = consultationStartTime.current || (() => {
        const saved = sessionStorage.getItem('consultationStartTime');
        return saved ? parseInt(saved, 10) : null;
      })();
      
      if (startTime) {
        // Calculate time elapsed in seconds
        const timeInSeconds = Math.floor((Date.now() - startTime) / 1000);
        
        // Get email from sessionStorage or use default
        const DEFAULT_EMAIL = 'anonymous-eu@noaddress.ea';
        const email = sessionStorage.getItem('consultationEmail') || DEFAULT_EMAIL;
        
        // Get current language
        const language = i18n.language || 'en';
        
        // Only submit if language has completion field configured
        if (hasCompletionField(language)) {
          // Fire and forget - don't await, don't block completion flow
          submitCompletionToEN(email, timeInSeconds, language).catch((error) => {
            console.error('[MainApplication] Failed to submit completion to EN:', error);
            // Silent failure - don't block user flow
          });
        } else {
          console.log('[MainApplication] Completion field not configured for language:', language);
        }
      } else {
        console.warn('[MainApplication] No start time found, skipping completion submission');
      }
    } catch (error) {
      console.error('[MainApplication] Error preparing completion submission:', error);
      // Silent failure - don't block user flow
    }
    
    // Clear start time from sessionStorage
    sessionStorage.removeItem('consultationStartTime');
    
    onComplete();
  };

  const handlePrevious = () => {
    if (currentQuestionIndex === 0 && onNavigateToHowToUse) {
      // On Q1, navigate back to HowToUse screen
      onNavigateToHowToUse();
    } else if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Help Modal */}
      <HelpModal 
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Confirmation Modal */}
      <Dialog open={showConfirmationModal} onOpenChange={setShowConfirmationModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="sr-only">{t('confirmation.title')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 overflow-hidden">
            <div className="bg-[#FF6F4E]/10 border-2 border-[#FF6F4E] rounded-lg p-6 break-words">
              <p className="text-[18px] mb-3">
                <strong>{t('confirmation.title')}</strong>
              </p>
              <p className="text-[16px] mb-3">
                {t('confirmation.message1')}
              </p>
              <p className="text-[16px]">
                {t('confirmation.message2')}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={handleConfirmCompletion}
              className="bg-[#FF6F4E] text-black hover:bg-[#FF6F4E]/90 font-bold text-[16px] w-full sm:w-auto min-h-[48px] px-6"
            >
              {t('confirmation.continueButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header with Progress */}
      <PageHeader
        currentQuestion={currentQuestionIndex + 1}
        totalQuestions={config.steps.length}
        onHelpClick={() => setShowHelpModal(true)}
      />

      {/* Instructions Section */}
      <div className="bg-white border-b border-border">
        <div className="max-w-[900px] mx-auto px-8">
          <Collapsible open={isInstructionsOpen} onOpenChange={setIsInstructionsOpen}>
            <CollapsibleTrigger className="w-full py-4 flex items-center justify-between hover:opacity-80 transition-opacity">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-[#FF6F4E]" />
                <span className="font-bold text-[18px]">{t('instructions.title')}</span>
              </div>
              {isInstructionsOpen ? (
                <ChevronUp className="w-5 h-5 text-[#FF6F4E]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#FF6F4E]" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="pb-6">
              {/* Desktop Instructions - Hidden on screens 900px and below */}
              <div className="hidden lg:block">
                <DesktopInstructions 
                  onOpenConsultation={handleOpenConsultation}
                />
              </div>

              {/* Mobile Instructions - Hidden on screens above 900px */}
              <div className="block lg:hidden">
                <MobileInstructions
                  onOpenConsultation={handleOpenConsultation}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      {/* ARIA Live Region for Step Changes */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
      >
        {currentTranslation && `Step ${currentQuestionIndex + 1} of ${config.steps.length}: ${currentTranslation.questionText}`}
      </div>

      {/* Main Content */}
      <main role="main" className="flex-1 w-full overflow-hidden">
        <div className="max-w-[900px] mx-auto h-full">
          <GuidancePanel
            step={currentStep}
            translation={currentTranslation}
            userCountry={userCountry}
          />
        </div>
      </main>

      {/* Navigation Footer */}
      {!showHelpModal && (
        <nav aria-label="Wizard navigation" className="bg-white border-t border-border w-full flex-shrink-0">
          <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-50">
            <div className="max-w-[1800px] mx-auto p-[15px] pr-[55px] flex items-center justify-between">
              <button
                onClick={handlePrevious}
                aria-label={`${t('navigation.previous')} - Step ${currentQuestionIndex} of ${config.steps.length}`}
                className="flex items-center gap-2 px-6 bg-muted text-foreground hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold min-h-[48px]"
              >
                <ChevronLeft className="w-5 h-5" />
                {t('navigation.previous')}
              </button>

              <button
                onClick={handleNext}
                aria-label={isLastQuestion 
                  ? `${t('navigation.complete')} - Step ${currentQuestionIndex + 1} of ${config.steps.length}`
                  : `${t('navigation.next')} - Step ${currentQuestionIndex + 1} of ${config.steps.length}`
                }
                className="flex items-center gap-2 px-6 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-bold min-h-[48px]"
              >
                {isLastQuestion ? t('navigation.complete') : t('navigation.next')}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </nav>
      )}
    </div>
  );
}
